import { SignAssociation } from '../components/stock/sign-associations/edit-association/sign-association';
import Asset from '../model/asset';
import { BoardTypeChoices } from '../model/product/board/board-part';
import IPBoard from '../model/product/ip-board/ip-board';
import IPBoardPart from '../model/product/ip-board/ip-board-part';
import { IP_BOARD_PART_TYPE_CHOICES } from '../model/product/ip-board/ip-board-part-type';
import { IPBoardPlacementTypeChoices } from '../model/product/ip-board/ip-board-placement-type';
import { SignDetail } from '../model/sign/sign-detail';
import { SignTypesChoices } from '../model/sign/enums/sign-type';
import { SignStickerDetail } from '../model/sign/sticker/sign-sticker-detail';
import { ClientIPBoardAndPlacementHelper } from '../shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { CollectionHelper } from './collection-helper';
import { GeneralHelper } from './general-helper';
import { StringHelper } from './string-helper';
import IPBoardPlacement from '../model/product/ip-board/ip-board-placement';
import { IPBoardPartPhoneNumber } from '../model/product/ip-board/ip-board-part-phone-number';
import { BooleanHelper } from './boolean-helper';
import { SignHelper } from './sign-helper';
import { SignPersonalizedTextTypesChoices } from '../model/sign/enums/sign-personalized-text-type';
import { KeysOfType } from './utility-functions';
import { SignSelectedEvent } from '../services/project-display-observable.service';
import { Project } from '../model/project/project';
import ProductHelper from './product-helper';
import Product from '../model/product/product';

export class IPBoardHelper {
  private constructor() {}

  static readonly IMPRESSION_FORM_SECTION = 'extraDesign';

  static readonly IP_70_100_PRODUCTION_PRICE = 30;
  static readonly IP_100_140_PRODUCTION_PRICE = 50;
  static readonly ADHESIVE_50_140__70_100_PRODUCTION_PRICE = 45;
  static readonly ADHESIVE_100_140__70_180_PRODUCTION_PRICE = 60;
  static readonly COLLABORATIVE_PRODUCTION_PRICE = 50;

  static readonly EXTRA_BOARD_PRICE = 20;

  static readonly BASE_CUSTOM_ADJUSTMENT_PRICE = 10;

  static readonly IP_BOARD_SECOND_BOARD_PLACEMENT_DISCOUNT = 45;
  static readonly ADHESIVE_BOARD_SECOND_BOARD_PLACEMENT_DISCOUNT = 30;

  static readonly FIXED_SOLD_RENTED_EXECUTION_PRICE = 15;

  static getTotalProductionPrice(
    ipBoard: IPBoard,
    ipBoardPart: IPBoardPart,
    collaborative: boolean
  ): number {
    const chargeTwice =
      !ipBoard.leftRightBoard &&
      ipBoardPart.boardType != null &&
      (ipBoardPart.boardType.ordinal === BoardTypeChoices.V.ordinal ||
        ipBoardPart.boardType.ordinal ===
          BoardTypeChoices.DOUBLE_SIDED.ordinal);
    const prices = [
      this.getCustomProductionPrice(ipBoard, collaborative, chargeTwice),
      this.getTextPrice(ipBoard, chargeTwice),
      this.getCustomAdjustmentStickerPrice(
        ipBoard,
        chargeTwice,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.TITLE_FROM_STOCK,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_TITLE,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_TITLE_FROM_STOCK,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_OTHER_TITLE
      ),
      this.getCustomAdjustmentStickerPrice(
        ipBoard,
        chargeTwice,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.PHONE_NUMBER_FROM_STOCK,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_PHONE_NUMBER,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_PHONE_NUMBER_FROM_STOCK,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_OTHER_PHONE_NUMBER
      ),
      this.getCustomAdjustmentStickerPrice(
        ipBoard,
        chargeTwice,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
        CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER,
        null,
        null
      ),
      this.getExtraBoardPrice(ipBoard)
    ];
    return prices.reduce(GeneralHelper.addGuardNull);
  }

  static getCustomProductionPrice(
    ipBoard: IPBoard,
    collaborative: boolean,
    chargeTwice: boolean
  ): number {
    if (ipBoard.boardPart == null) {
      return null;
    }

    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      return this.getCustomProductionPriceForBoardPart(
        ipBoard.boardPart,
        collaborative,
        chargeTwice
      );
    }

    [
      this.getCustomProductionPriceForBoardPart(
        ipBoard.boardPart,
        collaborative,
        chargeTwice
      ),
      this.getCustomProductionPriceForBoardPart(
        ipBoard.otherSideBoardPart,
        collaborative,
        chargeTwice
      )
    ].reduce(GeneralHelper.addGuardNull);
  }

  private static getCustomProductionPriceForBoardPart(
    ipBoardPart: IPBoardPart,
    collaborative: boolean,
    chargeTwice: boolean
  ): number {
    const chargeProductionTwice =
      chargeTwice &&
      !ClientIPBoardAndPlacementHelper.isAdhesiveBoard(ipBoardPart);
    if (
      ipBoardPart == null ||
      ipBoardPart.sign != null ||
      ipBoardPart.otherIPBoardPartType == null
    ) {
      if (ipBoardPart.sign != null && ipBoardPart.sign.withoutStock) {
        return chargeProductionTwice
          ? 2 * parseFloat(ipBoardPart.sign.singleBoardPrice)
          : parseFloat(ipBoardPart.sign.singleBoardPrice);
      }
      // only custom IP boards or adhesive boards have a production price, stock sign production pricing is handled elsewhere
      return null;
    }
    if (collaborative) {
      return chargeProductionTwice
        ? 2 * this.COLLABORATIVE_PRODUCTION_PRICE
        : this.COLLABORATIVE_PRODUCTION_PRICE;
    }
    switch (ipBoardPart.otherIPBoardPartType.ordinal) {
      case IP_BOARD_PART_TYPE_CHOICES.IP_70_100.ordinal:
        return chargeProductionTwice
          ? 2 * this.IP_70_100_PRODUCTION_PRICE
          : this.IP_70_100_PRODUCTION_PRICE;
      case IP_BOARD_PART_TYPE_CHOICES.IP_100_140.ordinal:
        return chargeProductionTwice
          ? 2 * this.IP_100_140_PRODUCTION_PRICE
          : this.IP_100_140_PRODUCTION_PRICE;
      case IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal:
        if (
          (ipBoardPart.otherWidth === 140 && ipBoardPart.otherHeight === 50) ||
          (ipBoardPart.otherWidth === 100 && ipBoardPart.otherHeight === 70)
        ) {
          return chargeProductionTwice
            ? 2 * this.ADHESIVE_50_140__70_100_PRODUCTION_PRICE
            : this.ADHESIVE_50_140__70_100_PRODUCTION_PRICE;
        } else if (
          (ipBoardPart.otherWidth === 140 && ipBoardPart.otherHeight === 100) ||
          (ipBoardPart.otherWidth === 180 && ipBoardPart.otherHeight === 70)
        ) {
          return chargeProductionTwice
            ? 2 * this.ADHESIVE_100_140__70_180_PRODUCTION_PRICE
            : this.ADHESIVE_100_140__70_180_PRODUCTION_PRICE;
        } else {
          return null;
        }
      default:
        return null;
    }
  }

  static getTextPrice(ipBoard: IPBoard, chargeTwice: boolean): number {
    if (ipBoard.boardPart == null) {
      return null;
    }

    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      return this.getTextPriceForBoardPart(ipBoard.boardPart, chargeTwice);
    }

    return [
      this.getTextPriceForBoardPart(ipBoard.boardPart, chargeTwice),
      this.getTextPriceForBoardPart(ipBoard.otherSideBoardPart, chargeTwice)
    ].reduce(GeneralHelper.addGuardNull);
  }

  static getTextPriceForBoardPart(
    ipBoardPart: IPBoardPart,
    chargeTwice: boolean
  ): number {
    if (
      ipBoardPart == null ||
      ipBoardPart.sign == null || // text price is only a factor for stock signs
      ipBoardPart.sign.textPrice == null ||
      (ipBoardPart.sign.withoutStock &&
        ipBoardPart.sign.textType != null &&
        ipBoardPart.sign.textType.value ===
          SignPersonalizedTextTypesChoices.STICK_ON_SIGN.value) ||
      (StringHelper.isEmpty(ipBoardPart.customText) &&
        StringHelper.isEmpty(ipBoardPart.otherCustomText))
    ) {
      return null;
    }
    return chargeTwice
      ? 2 * Number(ipBoardPart.sign.textPrice)
      : Number(ipBoardPart.sign.textPrice);
  }

  static representSameAdjustment(
    one: SignAssociation,
    other: SignAssociation
  ): boolean {
    if (one == null && other == null) {
      return true;
    }
    if (one == null || other == null) {
      return false;
    }
    return one.id === other.id;
  }

  static getCustomAdjustmentStickerPrice(
    ipBoard: IPBoard,
    chargeTwice: boolean,
    oneSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    oneSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY
  ): number {
    if (ipBoard.boardPart == null) {
      return null;
    }

    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      return this.getCustomAdjustmentStickerPriceForBoardPart(
        ipBoard.boardPart,
        chargeTwice,
        oneSideFromStockProperty,
        oneSideCustomProperty,
        otherSideFromStockProperty,
        otherSideCustomProperty
      );
    }

    return [
      this.getCustomAdjustmentStickerPriceForBoardPart(
        ipBoard.boardPart,
        chargeTwice,
        oneSideFromStockProperty,
        oneSideCustomProperty,
        otherSideFromStockProperty,
        otherSideCustomProperty
      ),
      this.getCustomAdjustmentStickerPriceForBoardPart(
        ipBoard.otherSideBoardPart,
        chargeTwice,
        oneSideFromStockProperty,
        oneSideCustomProperty,
        otherSideFromStockProperty,
        otherSideCustomProperty
      )
    ].reduce(GeneralHelper.addGuardNull);
  }

  static getCustomAdjustmentStickerPriceForBoardPart(
    ipBoardPart: IPBoardPart,
    chargeTwice: boolean,
    oneSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    oneSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY
  ): number {
    if (
      ipBoardPart == null ||
      ipBoardPart.sign == null ||
      ipBoardPart.sign.withoutStock
    ) {
      // this price only applies for stock signs and user has chosen a custom adjustment or sticker
      return null;
    }
    const oneSideFromStock =
      oneSideFromStockProperty == null
        ? null
        : ipBoardPart[oneSideFromStockProperty];
    const oneSideCustom: string =
      oneSideCustomProperty == null
        ? null
        : <string>ipBoardPart[oneSideCustomProperty];
    const otherSideFromStock =
      otherSideFromStockProperty == null
        ? null
        : ipBoardPart[otherSideFromStockProperty];
    const otherSideCustom: string =
      otherSideCustomProperty == null
        ? null
        : <string>ipBoardPart[otherSideCustomProperty];
    if (
      (oneSideFromStock != null || StringHelper.isEmpty(oneSideCustom)) &&
      (otherSideFromStock != null || StringHelper.isEmpty(otherSideCustom))
    ) {
      return null;
    } else if (
      oneSideFromStock == null &&
      !StringHelper.isEmpty(oneSideCustom) &&
      otherSideFromStock == null &&
      !StringHelper.isEmpty(otherSideCustom) &&
      chargeTwice
    ) {
      return 2 * this.BASE_CUSTOM_ADJUSTMENT_PRICE;
    } else {
      return this.BASE_CUSTOM_ADJUSTMENT_PRICE;
    }
  }

  static getExtraBoardPrice(ipBoard: IPBoard): number {
    return ipBoard.extraBoard == null ? null : this.EXTRA_BOARD_PRICE;
  }

  static getSecondBoardPlacementDiscount(ipBoardPart: IPBoardPart): number {
    if (ipBoardPart == null) {
      return null;
    }
    return ClientIPBoardAndPlacementHelper.isAdhesiveBoard(ipBoardPart)
      ? this.ADHESIVE_BOARD_SECOND_BOARD_PLACEMENT_DISCOUNT
      : this.IP_BOARD_SECOND_BOARD_PLACEMENT_DISCOUNT;
  }

  static representsDivergentBoard(product: Product): boolean {
    return (
      product != null &&
      ProductHelper.isIPBoard(product) &&
      this.isDivergentBoard(product.boardPart)
    );
  }

  static isDivergentBoard(ipBoardPart: IPBoardPart): boolean {
    return (
      ipBoardPart != null &&
      ipBoardPart.boardType != null &&
      ipBoardPart.boardType.ordinal === BoardTypeChoices.DIVERGENT.ordinal
    );
  }

  static needsExtraDesign(ipBoard: IPBoard): boolean {
    return (
      ipBoard != null &&
      ipBoard.boardPart != null &&
      this.hasAmbiguousDesign(ipBoard.boardPart)
    );
  }

  static hasArrows(ipBoardPlacement: IPBoardPlacement): boolean {
    return (
      ipBoardPlacement != null &&
      ipBoardPlacement.arrows != null &&
      ((ipBoardPlacement.arrows.amountArrowsLeft != null &&
        ipBoardPlacement.arrows.amountArrowsLeft > 0) ||
        (ipBoardPlacement.arrows.amountArrowsRight != null &&
          ipBoardPlacement.arrows.amountArrowsRight > 0))
    );
  }

  static getLeftDisplayExample(boardPart: IPBoardPart): Asset {
    if (boardPart == null || boardPart.sign == null) {
      return null;
    }

    if (!this.leftNonStockAdjustment(boardPart)) {
      if (
        boardPart.titleAdjustment != null ||
        boardPart.phoneNumberAdjustment != null ||
        boardPart.sticker != null
      ) {
        return this.isExampleCandidate(boardPart.titleAdjustment)
          ? this.getFirstExample(boardPart.titleAdjustment)
          : this.isExampleCandidate(boardPart.phoneNumberAdjustment)
          ? this.getFirstExample(boardPart.phoneNumberAdjustment)
          : this.isExampleCandidate(boardPart.sticker)
          ? this.getFirstExample(boardPart.sticker)
          : null;
      } else {
        return this.getFirstSignExample(boardPart.sign);
      }
    }
    return null;
  }

  static getRightDisplayExample(ipBoard: IPBoard): Asset {
    if (ipBoard == null) {
      return null;
    }
    return ipBoard.leftRightBoard
      ? this.getLeftDisplayExample(ipBoard.otherSideBoardPart)
      : this.getSingleBoardPartRightDisplayExample(ipBoard);
  }

  private static getSingleBoardPartRightDisplayExample(
    ipBoard: IPBoard
  ): Asset {
    if (ipBoard.boardPart == null || ipBoard.boardPart.sign == null) {
      return null;
    }

    if (!this.rightNonStockAdjustment(ipBoard.boardPart)) {
      if (
        ipBoard.boardPart.otherTitleAdjustment != null ||
        ipBoard.boardPart.otherPhoneNumberAdjustment != null ||
        ipBoard.boardPart.sticker != null
      ) {
        return this.isExampleCandidate(ipBoard.boardPart.otherTitleAdjustment)
          ? this.getFirstExample(ipBoard.boardPart.otherTitleAdjustment)
          : this.isExampleCandidate(
              ipBoard.boardPart.otherPhoneNumberAdjustment
            )
          ? this.getFirstExample(ipBoard.boardPart.otherPhoneNumberAdjustment)
          : this.isExampleCandidate(ipBoard.boardPart.sticker)
          ? this.getFirstExample(ipBoard.boardPart.sticker)
          : null;
      } else {
        return this.getFirstSignExample(ipBoard.boardPart.sign);
      }
    }
    return null;
  }

  private static hasAmbiguousDesign(ipBoardPart: IPBoardPart): boolean {
    return (
      ipBoardPart.sign == null ||
      this.leftSideAmbiguousDesign(ipBoardPart) ||
      this.rightSideAmbiguousDesign(ipBoardPart)
    );
  }

  private static leftSideAmbiguousDesign(ipBoardPart: IPBoardPart): boolean {
    return (
      this.leftNonStockAdjustment(ipBoardPart) ||
      this.ambiguousExample(ipBoardPart)
    );
  }

  private static rightSideAmbiguousDesign(ipBoardPart: IPBoardPart): boolean {
    return (
      this.rightNonStockAdjustment(ipBoardPart) ||
      this.ambiguousOtherSideExample(ipBoardPart)
    );
  }

  private static leftNonStockAdjustment(ipBoardPart: IPBoardPart): boolean {
    return (
      !StringHelper.isEmpty(ipBoardPart.otherTitle) ||
      !StringHelper.isEmpty(ipBoardPart.otherPhoneNumber) ||
      !StringHelper.isEmpty(ipBoardPart.otherSticker)
    );
  }

  private static rightNonStockAdjustment(ipBoardPart: IPBoardPart): boolean {
    return (
      !StringHelper.isEmpty(ipBoardPart.otherSideOtherTitle) ||
      !StringHelper.isEmpty(ipBoardPart.otherSideOtherPhoneNumber) ||
      !StringHelper.isEmpty(ipBoardPart.otherSticker)
    );
  }

  private static ambiguousExample(ipBoardPart: IPBoardPart): boolean {
    return BooleanHelper.atLeastTwoOutOfThree(
      this.isExampleCandidate(ipBoardPart.titleAdjustment),
      this.isExampleCandidate(ipBoardPart.phoneNumberAdjustment),
      this.isExampleCandidate(ipBoardPart.sticker)
    );
  }

  private static ambiguousOtherSideExample(ipBoardPart: IPBoardPart): boolean {
    return BooleanHelper.atLeastTwoOutOfThree(
      this.isExampleCandidate(ipBoardPart.otherTitleAdjustment),
      this.isExampleCandidate(ipBoardPart.otherPhoneNumberAdjustment),
      this.isExampleCandidate(ipBoardPart.sticker)
    );
  }

  private static isExampleCandidate(signAssociation: SignAssociation): boolean {
    return (
      signAssociation != null &&
      CollectionHelper.isPopulated(signAssociation.examples) &&
      signAssociation.examples.some(e => !e.archived && e.asset != null) &&
      signAssociation.graphicsComplete
    );
  }

  private static getFirstExample(signAssociation: SignAssociation): Asset {
    const validExample = signAssociation.examples.find(
      e => e != null && e.asset != null
    );
    return validExample == null ? null : validExample.asset;
  }

  private static getFirstSignExample(sign: SignDetail): Asset {
    const validExample =
      sign.examples == null
        ? null
        : sign.examples.find(e => e != null && !e.archived && e.asset != null);
    return validExample == null ? null : validExample.asset;
  }

  static getTypeAndMeasurementsAndDescription(
    ipBoard: IPBoard,
    separator = '',
    abbreviatedKind = false
  ): string {
    if (ipBoard == null || ipBoard.boardPart == null) {
      return null;
    }

    let kind = ipBoard.boardPart.kind;
    if (kind != null) {
      kind = abbreviatedKind ? this.getAbbreviatedKind(kind) : kind;
    } else {
      kind = this.getIPBoardPartKind(ipBoard.boardPart);
      kind = abbreviatedKind ? this.getAbbreviatedKind(kind) : kind;
    }

    return StringHelper.join(
      [
        kind,
        this.isDivergentBoard(ipBoard.boardPart)
          ? null
          : this.getShortMeasurements(ipBoard),
        this.getShortDescription(ipBoard, separator)
      ],
      separator
    );
  }

  static getTypeAndMeasurements(
    ipBoard: IPBoard,
    separator = ' ',
    abbreviatedKind = false
  ): string {
    if (ipBoard == null || ipBoard.boardPart == null) {
      return null;
    }

    let kind = ipBoard.boardPart.kind;
    if (kind != null) {
      kind = abbreviatedKind ? this.getAbbreviatedKind(kind) : kind;
    } else {
      kind = this.getIPBoardPartKind(ipBoard.boardPart);
      kind = abbreviatedKind ? this.getAbbreviatedKind(kind) : kind;
    }

    if (
      !abbreviatedKind &&
      !StringHelper.isEmptyOrWhiteSpace(kind) &&
      kind.toLocaleLowerCase().trim() === 'ip'
    ) {
      kind = 'IP-bord';
    }

    return StringHelper.join(
      [kind, this.getShortMeasurements(ipBoard)],
      separator
    );
  }

  static getShortDescription(ipBoard: IPBoard, separator = ' '): string {
    if (ipBoard == null || ipBoard.boardPart == null) {
      return null;
    }

    const isDivergentBoard = this.isDivergentBoard(ipBoard.boardPart);

    return StringHelper.join(
      [
        ipBoard.boardPart.boardType == null
          ? null
          : ipBoard.boardPart.boardType.label,
        isDivergentBoard ? null : this.getPlacementType(ipBoard.boardPart),
        isDivergentBoard ? ipBoard.boardPart.divergentBoardType : null
      ],
      separator
    );
  }

  public static getPlacementType(boardPart: IPBoardPart): string {
    if (boardPart == null) {
      return null;
    }
    return boardPart.placementType == null ||
      boardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.OTHER.ordinal
      ? boardPart.alternativePlacementType
      : boardPart.placementType.label.toLowerCase();
  }

  static getShortMeasurements(ipBoard: IPBoard): string {
    if (ipBoard == null || ipBoard.boardPart == null) {
      return null;
    }

    if (ipBoard.boardPart.sign != null) {
      return SignHelper.getSignOrientationDisplay(
        ipBoard.boardPart.sign,
        false,
        false,
        true
      );
    } else {
      return (
        StringHelper.join(
          [
            ipBoard.boardPart.otherHeight == null
              ? null
              : ipBoard.boardPart.otherHeight.toString(),
            ipBoard.boardPart.otherWidth == null
              ? null
              : ipBoard.boardPart.otherWidth.toString()
          ],
          'x'
        ) + ' cm'
      );
    }
  }

  private static getIPBoardPartKind(boardPart: IPBoardPart): string {
    if (boardPart == null) {
      return null;
    }
    if (boardPart.sign != null) {
      return boardPart.sign.type == null ? null : boardPart.sign.type.label;
    } else if (boardPart.otherIPBoardPartType == null) {
      return SignTypesChoices.INPRESS.label;
    } else {
      return boardPart.otherIPBoardPartType.ordinal ===
        IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
        ? IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.label
        : SignTypesChoices.INPRESS.label;
    }
  }

  private static getAbbreviatedKind(kind: string): string {
    return kind === 'Kleefbord' ? 'KL' : kind;
  }

  static getSignStickerSummary(
    signSticker: SignStickerDetail,
    separator = ' / '
  ): string {
    if (signSticker == null) {
      return null;
    }
    return StringHelper.join(
      [
        signSticker.standard ? 'Standaard sticker' : 'Custom sticker',
        this.getSignStickerMeasurements(signSticker),
        StringHelper.isEmpty(signSticker.textColor)
          ? null
          : `tekst: ${signSticker.textColor}`,
        StringHelper.isEmpty(signSticker.foundationColor)
          ? null
          : `fond: ${signSticker.foundationColor}`
      ],
      separator
    );
  }

  static getSignStickerMeasurements(signSticker: SignStickerDetail): string {
    if (signSticker == null) {
      return null;
    }
    return StringHelper.join(
      [
        signSticker.height == null ? null : `${signSticker.height} cm`,
        signSticker.width == null ? null : `${signSticker.width} cm`
      ],
      ' x '
    );
  }

  static getPhoneNumberDisplay(phoneNumber: IPBoardPartPhoneNumber): string {
    if (phoneNumber == null) {
      return null;
    }
    return StringHelper.join(
      [
        phoneNumber.phoneNumber,
        phoneNumber.company == null ||
        StringHelper.isEmpty(phoneNumber.company.regularName)
          ? null
          : `(${phoneNumber.company.regularName})`
      ],
      ' '
    );
  }

  static needsPrintworkForConfig(
    ipBoardPart: IPBoardPart,
    adjustmentKey: KeysOfType<IPBoardPart, SignAssociation>,
    alternativeKey: KeysOfType<IPBoardPart, string>
  ): boolean {
    if (ipBoardPart.sign == null || ipBoardPart.sign.withoutStock) {
      return false;
    }
    if (ipBoardPart[adjustmentKey] != null) {
      return !ipBoardPart[adjustmentKey].inStock;
    }
    return !StringHelper.isEmptyOrWhiteSpace(ipBoardPart[alternativeKey]);
  }

  static needsPrintworkForText(ipBoardPart: IPBoardPart): boolean {
    return (
      ipBoardPart.sign != null && this.needsPrintworkForSign(ipBoardPart.sign)
    );
  }

  private static needsPrintworkForSign(sign: SignDetail): boolean {
    return (
      !sign.withoutStock ||
      (sign.textType != null &&
        (sign.textType.value ===
          SignPersonalizedTextTypesChoices.SEPARATE_SIGNS.value ||
          sign.textType.value ===
            SignPersonalizedTextTypesChoices.SEPARATE_SIGNS_ABOVE.value))
    );
  }

  static getNeededStockForBoardPart(
    ipBoardPart: IPBoardPart,
    doubleIfApplicable: boolean,
    isSecondBoardCopy: boolean
  ): number {
    if (ipBoardPart.sign == null || ipBoardPart.sign.withoutStock) {
      return 0;
    }
    const baseStock =
      doubleIfApplicable &&
      ipBoardPart.boardType != null &&
      (ipBoardPart.boardType.ordinal ===
        BoardTypeChoices.DOUBLE_SIDED.ordinal ||
        ipBoardPart.boardType.ordinal === BoardTypeChoices.V.ordinal) &&
      (ipBoardPart.sign != null &&
        (ipBoardPart.sign.type == null ||
          ipBoardPart.sign.type.value !== SignTypesChoices.STICKER.value))
        ? 2
        : 1;
    return baseStock * (isSecondBoardCopy ? 2 : 1);
  }

  static signSelectedEventRelevantForProject(
    event: SignSelectedEvent,
    project: Project
  ): boolean {
    if (
      event == null ||
      project == null ||
      !CollectionHelper.isPopulated(project.products)
    ) {
      return false;
    }
    for (const product of project.products) {
      if (
        ProductHelper.isIPBoard(product) &&
        ((product.boardPart != null &&
          (event.ipBoardPart.id === product.boardPart.id ||
            event.ipBoardPart === product.boardPart)) ||
          (product.otherSideBoardPart != null &&
            (event.ipBoardPart.id === product.otherSideBoardPart.id ||
              event.ipBoardPart === product.otherSideBoardPart)))
      ) {
        return true;
      }
    }
    return false;
  }

  // precondition: ipBoardPart.sign != null
  static rememberTextContents(ipBoardPart: IPBoardPart): void {
    IPBoardHelper.rememberSpecificTextContents(
      ipBoardPart,
      'previousTitleContent',
      'title',
      'titleAdjustment',
      'title',
      'otherTitle'
    );
    IPBoardHelper.rememberSpecificTextContents(
      ipBoardPart,
      'previousOtherSideTitleContent',
      'title',
      'otherTitleAdjustment',
      'title',
      'otherSideOtherTitle'
    );
    IPBoardHelper.rememberSpecificTextContents(
      ipBoardPart,
      'previousPhoneNumberContent',
      'phoneNumber',
      'phoneNumberAdjustment',
      'phoneNumber',
      'otherPhoneNumber'
    );
    IPBoardHelper.rememberSpecificTextContents(
      ipBoardPart,
      'previousOtherSidePhoneNumberContent',
      'phoneNumber',
      'otherPhoneNumberAdjustment',
      'phoneNumber',
      'otherSideOtherPhoneNumber'
    );
    IPBoardHelper.rememberSpecificTextContents(
      ipBoardPart,
      'previousStickerContent',
      null,
      'sticker',
      'text',
      'otherSticker'
    );
  }

  private static rememberSpecificTextContents(
    ipBoardPart: IPBoardPart,
    previousContentProp: KeysOfType<IPBoardPart, string>,
    defaultTextProp: KeysOfType<SignDetail, string>,
    assocProp: KeysOfType<IPBoardPart, SignAssociation>,
    assocTextProp: KeysOfType<SignAssociation, string>,
    otherTextProp: KeysOfType<IPBoardPart, string>
  ): void {
    const assoc = ipBoardPart[assocProp];
    if (assoc != null) {
      const text = assoc[assocTextProp];
      ipBoardPart[previousContentProp] = text;
    } else if (
      otherTextProp != null &&
      !StringHelper.isEmptyOrWhiteSpace(ipBoardPart[otherTextProp])
    ) {
      ipBoardPart[previousContentProp] = ipBoardPart[otherTextProp];
    } else if (
      defaultTextProp != null &&
      !StringHelper.isEmptyOrWhiteSpace(ipBoardPart.sign[defaultTextProp])
    ) {
      ipBoardPart[previousContentProp] = ipBoardPart.sign[defaultTextProp];
    } else {
      ipBoardPart[previousContentProp] = null;
    }
  }

  static rememberTextContentsFromCustomSign(ipBoardPart: IPBoardPart): void {
    ipBoardPart.previousTitleContent = ipBoardPart.otherTitle;
    ipBoardPart.previousOtherSideTitleContent = ipBoardPart.otherSideOtherTitle;
    ipBoardPart.previousPhoneNumberContent = ipBoardPart.otherPhoneNumber;
    ipBoardPart.previousOtherSidePhoneNumberContent =
      ipBoardPart.otherSideOtherPhoneNumber;
    ipBoardPart.previousStickerContent = ipBoardPart.otherSticker;
  }
}

export enum STOCK_ADJUSTMENT_STICKER_PROPERTY {
  TITLE_FROM_STOCK = 'titleAdjustment',
  OTHER_SIDE_TITLE_FROM_STOCK = 'otherTitleAdjustment',
  PHONE_NUMBER_FROM_STOCK = 'phoneNumberAdjustment',
  OTHER_SIDE_PHONE_NUMBER_FROM_STOCK = 'otherPhoneNumberAdjustment',
  STICKER_FROM_STOCK = 'sticker'
}

export enum CUSTOM_ADJUSTMENT_STICKER_PROPERTY {
  TITLE_FROM_STOCK = 'titleAdjustment',
  OTHER_TITLE = 'otherTitle',
  OTHER_SIDE_TITLE_FROM_STOCK = 'otherTitleAdjustment',
  OTHER_SIDE_OTHER_TITLE = 'otherSideOtherTitle',
  PHONE_NUMBER_FROM_STOCK = 'phoneNumberAdjustment',
  OTHER_PHONE_NUMBER = 'otherPhoneNumber',
  OTHER_SIDE_PHONE_NUMBER_FROM_STOCK = 'otherPhoneNumberAdjustment',
  OTHER_SIDE_OTHER_PHONE_NUMBER = 'otherSideOtherPhoneNumber',
  STICKER_FROM_STOCK = 'sticker',
  OTHER_STICKER = 'otherSticker'
}
